import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Clock, User, MapPin, Package, Edit2, X, LogIn, Download, Calendar, Trash2, UserX, 
  UserPlus, UserMinus, Archive, Box, Tag, Route, FileText,
  Truck, ScanLine, PackageCheck, PackagePlus, PackageMinus, PackageX, RotateCcw, Plus, Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const actionConfig = {
  // Authentication
  login: { label: "Login", color: "bg-blue-100 text-blue-800", icon: LogIn },
  change_password: { label: "Self Password Change", color: "bg-blue-100 text-blue-800", icon: User },
  // Security-critical: admin/manager reset another user's password. Red border
  // so it stands out at a glance in the log stream.
  admin_reset_password: {
    label: "Admin Reset Password",
    color: "bg-red-100 text-red-800 border-2 border-red-400",
    icon: User,
  },
  
  // Orders
  create_order: { label: "Create Order", color: "bg-green-100 text-green-800", icon: Package },
  edit_order: { label: "Edit Order", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  update_order: { label: "Update Order", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  update_order_status: { label: "Update Order Status", color: "bg-amber-100 text-amber-800", icon: Edit2 },
  cancel_order: { label: "Cancel Order", color: "bg-red-100 text-red-800", icon: X },
  delete_order: { label: "Delete Order", color: "bg-red-100 text-red-800", icon: Trash2 },
  permanent_delete_order: { label: "Permanently Delete Order", color: "bg-red-100 text-red-800", icon: Trash2 },
  
  // Users
  create_user: { label: "Create User", color: "bg-green-100 text-green-800", icon: UserPlus },
  edit_user: { label: "Edit User", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  delete_user: { label: "Delete User", color: "bg-red-100 text-red-800", icon: UserMinus },
  archive_user: { label: "Archive User", color: "bg-gray-100 text-gray-800", icon: Archive },
  unarchive_user: { label: "Unarchive User", color: "bg-gray-100 text-gray-800", icon: Archive },
  
  // Products
  create_product: { label: "Create Product", color: "bg-green-100 text-green-800", icon: Box },
  edit_product: { label: "Edit Product", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  delete_product: { label: "Delete Product", color: "bg-red-100 text-red-800", icon: Trash2 },
  
  // Categories
  create_category: { label: "Create Category", color: "bg-green-100 text-green-800", icon: Tag },
  edit_category: { label: "Edit Category", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  delete_category: { label: "Delete Category", color: "bg-red-100 text-red-800", icon: Trash2 },
  
  // Routes
  create_route: { label: "Create Route", color: "bg-green-100 text-green-800", icon: Route },
  edit_route: { label: "Edit Route", color: "bg-yellow-100 text-yellow-800", icon: Edit2 },
  delete_route: { label: "Delete Route", color: "bg-red-100 text-red-800", icon: Trash2 },

  // Dispatch (Bundle-based flow)
  dispatch_create_bundle:    { label: "Dispatch: Create Bag",         color: "bg-emerald-100 text-emerald-800", icon: PackagePlus },
  dispatch_delete_bundle:    { label: "Dispatch: Delete Bag",         color: "bg-red-100 text-red-800",         icon: PackageX },
  dispatch_scan:             { label: "Dispatch: Scan RFID",          color: "bg-sky-100 text-sky-800",         icon: ScanLine },
  dispatch_unscan:           { label: "Dispatch: Remove RFID",        color: "bg-orange-100 text-orange-800",   icon: PackageMinus },
  dispatch_manual_adjust:    { label: "Dispatch: Manual Count ±",     color: "bg-amber-100 text-amber-800",     icon: Plus },
  dispatch_complete_picking: { label: "Dispatch: Complete Picking",   color: "bg-indigo-100 text-indigo-800",   icon: PackageCheck },
  dispatch_complete_bundle:  { label: "Dispatch: Complete Check",     color: "bg-green-100 text-green-800",     icon: PackageCheck },
  dispatch_deliver_bundle:   { label: "Dispatch: Mark Delivered",     color: "bg-teal-100 text-teal-800",       icon: Truck },
  dispatch_reopen_picking:   { label: "Dispatch: Reopen to Picking",  color: "bg-yellow-100 text-yellow-800",   icon: RotateCcw },
  dispatch_reopen_check:     { label: "Dispatch: Reopen to Check",    color: "bg-yellow-100 text-yellow-800",   icon: RotateCcw },

  // Audit Logs
  delete_audit_log: { label: "Delete Audit Log", color: "bg-red-100 text-red-800", icon: FileText },
  delete_user_audit_logs: { label: "Delete User Audit Logs", color: "bg-red-100 text-red-800", icon: FileText },
};

export default function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [hotelNames, setHotelNames] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState("all");
  const [dateRange, setDateRange] = useState("all"); // "all", "today", "7days", "30days", "custom"
  const [actionFilter, setActionFilter] = useState("all"); // "all" | "passwords" | ...
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, username: null, logId: null });

  useEffect(() => {
    fetchAuditLogs();
  }, [selectedHotel, dateRange, customStartDate, customEndDate]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Hotel filter
      if (selectedHotel !== "all") {
        params.append("hotel_name", selectedHotel);
      }
      
      // Date filter
      const { startDate, endDate } = getDateRange();
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }
      
      const queryString = params.toString();
      const response = await axios.get(`${API}/audit-logs${queryString ? '?' + queryString : ''}`, {
        headers: getAuthHeader()
      });
      setLogs(response.data.logs);
      setHotelNames(response.data.hotel_names);
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateRange) {
      case "today":
        return {
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
      case "7days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return {
          startDate: sevenDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
      case "30days":
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return {
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        };
      case "custom":
        return {
          startDate: customStartDate || null,
          endDate: customEndDate || null
        };
      default: // "all"
        return { startDate: null, endDate: null };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const exportToCSV = () => {
    if (logs.length === 0) {
      toast.error("No logs to export");
      return;
    }

    // CSV Headers
    const headers = ["Date/Time", "Action", "User", "Hotel", "IP Address", "Order Number", "Details"];
    
    // CSV Rows
    const rows = logs.map(log => [
      formatTimestamp(log.timestamp),
      actionConfig[log.action]?.label || log.action,
      log.username,
      log.hotel_name || 'N/A',
      log.ip_address,
      log.order_number || 'N/A',
      log.details || 'N/A'
    ]);

    // Escape CSV values (handle commas and quotes)
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const filename = `audit_logs_${selectedHotel === 'all' ? 'all' : selectedHotel}_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${logs.length} log entries`);
  };

  const handleDeleteSingleLog = async () => {
    if (!deleteDialog.logId) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/audit-logs/${deleteDialog.logId}`, {
        headers: getAuthHeader()
      });
      
      toast.success("Audit log deleted successfully");
      setDeleteDialog({ open: false, type: null, username: null, logId: null });
      fetchAuditLogs(); // Refresh the logs
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete audit log";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserLogs = async () => {
    if (!deleteDialog.username) return;

    setLoading(true);
    try {
      const response = await axios.delete(`${API}/audit-logs/user/${deleteDialog.username}`, {
        headers: getAuthHeader()
      });
      
      toast.success(`Deleted ${response.data.deleted_count} log(s) for user "${deleteDialog.username}"`);
      setDeleteDialog({ open: false, type: null, username: null, logId: null });
      fetchAuditLogs(); // Refresh the logs
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete user logs";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.type === "single") {
      await handleDeleteSingleLog();
    } else if (deleteDialog.type === "all") {
      await handleDeleteUserLogs();
    }
  };

  // Client-side action filter (applied AFTER server-side hotel/date filters).
  const passwordActions = new Set(["change_password", "admin_reset_password"]);
  const visibleLogs = actionFilter === "passwords"
    ? logs.filter((l) => passwordActions.has(l.action))
    : logs;
  const passwordEventCount = logs.filter((l) => passwordActions.has(l.action)).length;

  // Group logs by date
  const groupedLogs = visibleLogs.reduce((groups, log) => {
    const date = new Date(log.timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
    return groups;
  }, {});

  // Get unique usernames from logs for deletion dropdown
  const uniqueUsernames = [...new Set(logs.map(log => log.username))].sort();

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Audit Logs</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Track user activities: logins, order creation, edits, and cancellations
              </p>
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                    disabled={uniqueUsernames.length === 0}
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Delete User Logs
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <DropdownMenuLabel>Select User to Delete All Logs</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {uniqueUsernames.map((username) => {
                    const userLogCount = logs.filter(log => log.username === username).length;
                    return (
                      <DropdownMenuItem
                        key={username}
                        onClick={() => setDeleteDialog({ 
                          open: true, 
                          type: "all", 
                          username: username, 
                          logId: null 
                        })}
                        className="cursor-pointer py-3"
                      >
                        <User className="w-4 h-4 mr-3 flex-shrink-0" />
                        <span className="flex-1 truncate font-medium">{username}</span>
                        <Badge variant="secondary" className="ml-3 text-xs flex-shrink-0">
                          {userLogCount} {userLogCount === 1 ? 'log' : 'logs'}
                        </Badge>
                      </DropdownMenuItem>
                    );
                  })}
                  {uniqueUsernames.length === 0 && (
                    <div className="px-2 py-4 text-center text-sm text-gray-500">
                      No users found
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                disabled={logs.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-end gap-4 pt-2 border-t">
            <div className="flex-shrink-0">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                <User className="w-3 h-3 inline mr-1" />
                Event Type
              </Label>
              <div className="flex gap-1">
                <Button
                  variant={actionFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActionFilter("all")}
                  data-testid="audit-filter-all"
                >
                  All Events
                </Button>
                <Button
                  variant={actionFilter === "passwords" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActionFilter("passwords")}
                  className={actionFilter === "passwords" ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-700 border-red-300 hover:bg-red-50"}
                  data-testid="audit-filter-passwords"
                >
                  Password Events
                  {passwordEventCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-red-700">
                      {passwordEventCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex-1">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                <MapPin className="w-3 h-3 inline mr-1" />
                Hotel
              </Label>
              <Select value={selectedHotel} onValueChange={setSelectedHotel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hotels</SelectItem>
                  {hotelNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                <Calendar className="w-3 h-3 inline mr-1" />
                Date Range
              </Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="7days">Last 7 Days</SelectItem>
                  <SelectItem value="30days">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateRange === "custom" && (
              <>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading logs...</div>
        ) : visibleLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {actionFilter === "passwords"
              ? "No password events in the selected range 🎉"
              : "No audit logs found"}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedLogs).map(([date, dateLogs]) => (
              <div key={date}>
                <h3 className="font-semibold text-lg mb-3 text-gray-800 border-b pb-2">
                  {date}
                </h3>
                <div className="space-y-2">
                  {dateLogs.map((log) => {
                    const config = actionConfig[log.action] || { 
                      label: log.action, 
                      color: "bg-gray-100 text-gray-800", 
                      icon: Package 
                    };
                    const Icon = config.icon;

                    return (
                      <div
                        key={log.id}
                        className="flex items-start gap-2 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          <div className={`p-1.5 sm:p-2 rounded-full ${config.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={`${config.color} text-[10px] sm:text-xs px-1.5 py-0.5`}>
                              {config.label}
                            </Badge>
                            <span className="text-[11px] sm:text-sm text-gray-500 flex items-center gap-1 shrink-0">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(log.timestamp)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 sm:gap-y-1 text-[13px] sm:text-sm">
                            <div className="flex items-center gap-1 min-w-0">
                              <User className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="text-gray-600 shrink-0">User:</span>
                              <span className="font-medium truncate">{log.username}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-0">
                              <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="text-gray-600 shrink-0">Hotel:</span>
                              <span className="font-medium truncate">{log.hotel_name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="text-gray-600 shrink-0">IP:</span>
                              <span className="font-mono text-[11px] bg-gray-100 px-1.5 py-0.5 rounded truncate">
                                {log.ip_address}
                              </span>
                            </div>
                            {log.order_number && (
                              <div className="flex items-center gap-1 min-w-0">
                                <Package className="w-3 h-3 text-gray-400 shrink-0" />
                                <span className="text-gray-600 shrink-0">Order:</span>
                                <span className="font-medium truncate">#{log.order_number}</span>
                              </div>
                            )}
                          </div>
                          
                          {log.details && (
                            <div className="mt-2 text-[12px] sm:text-sm text-gray-600 bg-gray-50 p-2 rounded break-words">
                              {log.details}
                            </div>
                          )}
                        </div>

                        <div className="flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteDialog({ 
                              open: true, 
                              type: "single", 
                              username: log.username, 
                              logId: log.id 
                            })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            title="Delete this log"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, type: null, username: null, logId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialog.type === "single" ? "Delete This Log?" : "Delete All Logs for User?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === "single" ? (
                <>
                  <p className="mb-2">Are you sure you want to delete this audit log entry?</p>
                  <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                    <p className="font-semibold text-gray-800">User: {deleteDialog.username}</p>
                  </div>
                  <p className="mt-3 text-orange-600 font-semibold">
                    ⚠️ This action cannot be undone!
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2">Are you sure you want to delete ALL audit logs for this user?</p>
                  <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                    <p className="font-semibold text-gray-800">User: {deleteDialog.username}</p>
                  </div>
                  <p className="mt-3 text-red-600 font-semibold">
                    ⚠️ This action cannot be undone! All activity history for this user will be permanently deleted.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, type: null, username: null, logId: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Deleting..." : deleteDialog.type === "single" ? "Yes, Delete This Log" : "Yes, Delete All Logs"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
